import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);

    // Under test, refuse to absorb the crash (#510).
    //
    // Catching a render error and showing a calm panel is exactly right for an
    // operator and exactly wrong for a test suite: #508 crashed the entire UI
    // on load and 627 tests stayed green, because this boundary turned a
    // TypeError into a rendered <div> that nothing asserted against.
    //
    // Rethrowing here makes any such regression a failing test rather than a
    // screenshot from production. Guarded on MODE === 'test' so the operator
    // still gets the panel; Vitest sets it, `pnpm dev` and a built bundle do
    // not.
    if (import.meta.env?.MODE === 'test') {
      throw error;
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            padding: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" gutterBottom>
            Something went wrong
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {this.state.error?.message || 'An unexpected error occurred'}
          </Typography>
          <Button
            variant="contained"
            onClick={this.handleReset}
            sx={{ mt: 2 }}
          >
            Reload Application
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
